import type { CSSProperties, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Skeleton } from '@mantine/core';
import classes from './HomePanel.module.css';

/** Сколько сделок полоса рисует отдельными отрезками. */
const MAX_SEGMENTS = 20;

/** Раскладка активных сделок для полосы под числом. mineLate — опоздавшие с моим ходом, входят в mine и late. */
export interface PanelBar {
  readonly mine: number;
  readonly late: number;
  readonly mineLate: number;
  readonly others: number;
}

export interface PanelBlock {
  readonly key: string;
  readonly to: string;
  readonly icon: ReactNode;
  readonly title: string;
  readonly loading?: boolean;
  /** Данные не загрузились: вместо числа «Нет данных», ноль не выдаём. */
  readonly failed?: boolean;
  readonly value?: number;
  /** Слово после числа в нужной форме: «прайс-листа», «сделок». */
  readonly unit?: string;
  /** Призыв вместо нуля: «Создать прайс-лист». */
  readonly emptyCta?: string;
  /** Строки под числом; пустые не выводятся. */
  readonly lines?: readonly (string | null | undefined)[];
  readonly bar?: PanelBar;
}

// Панель «Главной»: блоки-ссылки на разделы. Нулевое не показываем, при сбое пишем «Нет данных».
export default function HomePanel({ blocks }: { readonly blocks: readonly PanelBlock[] }) {
  const style = { '--panel-columns': blocks.length } as CSSProperties;
  return (
    <div className={`${classes.panel} on-dark`} style={style}>
      {blocks.map((block) => (
        <PanelBlockLink key={block.key} block={block} />
      ))}
    </div>
  );
}

function PanelBlockLink({ block }: { readonly block: PanelBlock }) {
  const { t } = useTranslation();
  const lines = (block.lines ?? []).filter((line): line is string => Boolean(line));

  return (
    <Link to={block.to} className={classes.block} aria-busy={block.loading || undefined}>
      <span className={classes.icon} aria-hidden>
        {block.icon}
      </span>
      <span className={classes.title}>{block.title}</span>
      {block.loading ? (
        <Skeleton h={40} w={120} mt={4} opacity={0.3} />
      ) : block.failed || block.value == null ? (
        <span className={classes.soft}>{t('app.home.noData')}</span>
      ) : block.value === 0 && block.emptyCta ? (
        <span className={classes.cta}>{block.emptyCta}</span>
      ) : (
        <>
          <span className={classes.soft}>
            <span className={classes.value}>{block.value}</span>
            {block.unit}
          </span>
          {lines.map((line) => (
            <span key={line} className={classes.soft}>
              {line}
            </span>
          ))}
          {block.bar ? <DealsBar bar={block.bar} /> : null}
        </>
      )}
    </Link>
  );
}

function DealsBar({ bar }: { readonly bar: PanelBar }) {
  const { t } = useTranslation();
  // На полосе каждая сделка — один отрезок: опоздание важнее хода, поэтому опоздавшая с моим ходом — розовая.
  const mineOnly = bar.mine - bar.mineLate;
  const total = mineOnly + bar.late + bar.others;
  if (total === 0) {
    return null;
  }
  // Подпись называет только ненулевые части: «0 опаздывает» не пишем.
  const legend = [
    bar.mine > 0 ? t('app.home.panel.waitingYou', { count: bar.mine }) : null,
    bar.late > 0 ? t('app.home.panel.late', { count: bar.late }) : null,
  ].filter(Boolean);
  const label = [
    ...legend,
    bar.others > 0 ? t('app.home.panel.waitingOthers', { count: bar.others }) : null,
  ]
    .filter(Boolean)
    .join(', ');
  // До 20 сделок — отрезок на сделку, как в образце; больше — три части по долям, иначе промежутки съели бы полосу.
  const parts: readonly (readonly [string, number])[] = [
    [`${classes.barMine}`, mineOnly],
    [`${classes.barLate}`, bar.late],
    [`${classes.barOthers}`, bar.others],
  ];
  const segments =
    total <= MAX_SEGMENTS
      ? parts.flatMap(([className, count]) => Array.from({ length: count }, () => ({ className, grow: 1 })))
      : parts.filter(([, count]) => count > 0).map(([className, count]) => ({ className, grow: count }));

  return (
    <>
      <span className={classes.bar} role="img" aria-label={label}>
        {segments.map((segment, index) => (
          // Отрезки не переставляются: номер — устойчивый ключ.
          <i key={index} className={segment.className} style={{ flexGrow: segment.grow }} />
        ))}
      </span>
      {legend.length > 0 ? (
        <span className={classes.legend} aria-hidden>
          {legend.map((part, index) => (
            // Часть подписи не разрывается, а точка остаётся в конце строки: перенос идёт между частями.
            <span key={part} className={classes.nowrap}>
              {part}
              {index < legend.length - 1 ? ' ·' : ''}
            </span>
          ))}
        </span>
      ) : null}
    </>
  );
}
